import { memo } from 'react';

import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SxProps,
  Theme,
  Tooltip,
} from '@mui/material';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { InfoAlert } from '../uiComponents/infoAlert';

const customStyles: Record<string, SxProps<Theme>> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '50%',
  },
  formControlText: {
    marginTop: '12px',
    marginLeft: '0px',
  },
  select: {
    height: '34px',
  },
  mobileOnlyAlert: {
    marginTop: '16px',
    marginBottom: '8px',
    width: '100%',
  },
  menuItem: {
    fontSize: '12px',
  },
  formatCheckbox: {
    padding: '0px 5px',
  },
};

const maxFilesQuantity = 5;
const FileSetting = memo((props: any) => {
  const {
    questionId,
    formats,
    onFileSettingChange,
    quantity,
    maximumAudioDurationInSeconds,
    tableId,
  } = props;
  const lang = useLanguage();
  const { checkFlag } = useFeatureFlags();
  const isVoiceAudioRecordingEnabled = checkFlag('VoiceAudioRecording');
  const baseOptions = [
    'any', // includes all formats except Voice Audio Recording
    'Audio',
    'CSV',
    'Excel',
    'PDF',
    'Video',
    'Word',
    'zip',
  ];

  const options = isVoiceAudioRecordingEnabled
    ? [...baseOptions.slice(0, 4), 'Voice Audio Recording', ...baseOptions.slice(4)]
    : baseOptions;

  const isVoiceAudioRecordingSelected = formats?.includes('Voice Audio Recording');

  const formatVoiceAudioRecordingDuration = (minutes: number, remainingSeconds: number) => {
    const secondsLabel = lang.form_builder.secondsLabel;
    const minutesLabel =
      minutes === 1 ? lang.form_builder.minuteLabel : lang.form_builder.minutesLabel;

    if (minutes > 0 && remainingSeconds > 0) {
      return `${minutes} ${minutesLabel} ${lang.form_builder.and} ${remainingSeconds} ${secondsLabel}`;
    }
    if (minutes > 0) {
      return `${minutes} ${minutesLabel}`;
    }
    return `${remainingSeconds} ${secondsLabel}`;
  };

  const generateVoiceAudioRecordingDurationOptions = () => {
    const options: { value: number; label: string }[] = [];

    for (let seconds = 30; seconds <= 10 * 60; seconds += 30) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      const label = formatVoiceAudioRecordingDuration(minutes, remainingSeconds);
      options.push({ value: seconds, label });
    }

    return options;
  };

  const voiceAudioRecordingDurationOptions = generateVoiceAudioRecordingDurationOptions();

  const getFileFormatDisplayLabel = (formatValue: string) => {
    if (formatValue === 'any') {
      return lang.form_builder.any;
    }
    if (formatValue === 'Voice Audio Recording') {
      return lang.form_builder.voiceAudioRecording;
    }
    return formatValue;
  };

  const handleFormatChange = (event: { target: { value: string[] } }) => {
    const { value } = event.target;
    const newFormatsWillIncludeVoiceAudio = value.includes('Voice Audio Recording');
    const currentFormatsIncludeVoiceAudio = formats?.includes('Voice Audio Recording');

    if (newFormatsWillIncludeVoiceAudio && !currentFormatsIncludeVoiceAudio) {
      onFileSettingChange(questionId, 1, 'quantity', tableId); // restricts the maximum number of files to 1
      onFileSettingChange(questionId, 30, 'maximumAudioDurationInSeconds', tableId);
      return onFileSettingChange(questionId, ['Voice Audio Recording'], 'formats', tableId);
    }

    if (!newFormatsWillIncludeVoiceAudio && currentFormatsIncludeVoiceAudio) {
      onFileSettingChange(questionId, undefined, 'maximumAudioDurationInSeconds', tableId);
    }

    if (currentFormatsIncludeVoiceAudio && newFormatsWillIncludeVoiceAudio && value.length > 1) {
      return;
    }

    if (formats.includes('any') && value.length > 1) {
      return onFileSettingChange(
        questionId,
        value.filter((v: string) => v !== 'any'),
        'formats',
        tableId,
      );
    }
    if (!formats.includes('any') && value.includes('any')) {
      return onFileSettingChange(questionId, ['any'], 'formats', tableId);
    }
    if (value.length === 0) {
      return onFileSettingChange(questionId, ['any'], 'formats', tableId);
    }
    return onFileSettingChange(questionId, value, 'formats', tableId);
  };

  return (
    <>
      <Box sx={customStyles.root}>
        <FormControl>
          <FormHelperText sx={customStyles.formControlText}>
            {lang.form_builder.formatType}
          </FormHelperText>
          <Select
            multiple
            disableUnderline
            value={formats}
            autoWidth
            sx={customStyles.select}
            SelectDisplayProps={{ style: { whiteSpace: 'pre-wrap' } }}
            MenuProps={{
              disableAutoFocusItem: true,
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            }}
            //@ts-ignore
            onChange={handleFormatChange}
            //@ts-ignore
            renderValue={(selected: string[]) => {
              return (
                <>
                  {selected.map((value: string) => (
                    <Chip
                      key={value}
                      label={getFileFormatDisplayLabel(value)}
                      size={'small'}
                    />
                  ))}
                </>
              );
            }}
          >
            {options.map((option) => {
              const isDisabled =
                isVoiceAudioRecordingSelected &&
                option !== 'Voice Audio Recording' &&
                isVoiceAudioRecordingEnabled;
              const showAnyFileFormatTooltip = formats.includes('any') && option === 'any';
              const menuItem = (
                <MenuItem
                  key={option}
                  value={option}
                  sx={customStyles.menuItem}
                  disabled={isDisabled}
                >
                  <Checkbox
                    color={'primary'}
                    checked={formats.includes(option)}
                    sx={customStyles.formatCheckbox}
                    size={'small'}
                    disabled={isDisabled}
                  />
                  {getFileFormatDisplayLabel(option)}
                </MenuItem>
              );

              if (isDisabled) {
                return (
                  <Tooltip
                    key={option}
                    title={lang.form_builder.cannotCombineWithVoiceAudioRecording}
                    placement='top'
                    arrow
                  >
                    <span>{menuItem}</span>
                  </Tooltip>
                );
              }

              if (showAnyFileFormatTooltip && isVoiceAudioRecordingEnabled) {
                return (
                  <Tooltip
                    key={option}
                    title={lang.form_builder.anyIncludeAllFormatsExceptVoiceAudioRecording}
                    placement='top'
                    arrow
                  >
                    <span>{menuItem}</span>
                  </Tooltip>
                );
              }

              return menuItem;
            })}
          </Select>
        </FormControl>

        {!isVoiceAudioRecordingSelected && (
          <FormControl>
            <FormHelperText sx={customStyles.formControlText}>
              {lang.form_builder.quantity}
            </FormHelperText>
            <Select
              disableUnderline
              value={quantity}
              sx={customStyles.select}
              onChange={(event) =>
                onFileSettingChange(questionId, event.target.value, 'quantity', tableId)
              }
            >
              {[...Array(maxFilesQuantity).keys()].map((filesQuantityIndex: number) => (
                <MenuItem
                  key={filesQuantityIndex}
                  sx={customStyles.menuItem}
                  value={filesQuantityIndex + 1}
                >
                  {filesQuantityIndex + 1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {isVoiceAudioRecordingSelected && isVoiceAudioRecordingEnabled && (
          <FormControl>
            <FormHelperText sx={customStyles.formControlText}>
              {lang.form_builder.audioDuration}
            </FormHelperText>
            <Select
              value={maximumAudioDurationInSeconds ?? 30}
              sx={customStyles.select}
              onChange={(event) =>
                onFileSettingChange(
                  questionId,
                  event.target.value,
                  'maximumAudioDurationInSeconds',
                  tableId,
                )
              }
            >
              {voiceAudioRecordingDurationOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  sx={customStyles.menuItem}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
      {isVoiceAudioRecordingSelected && isVoiceAudioRecordingEnabled && (
        <Box sx={customStyles.mobileOnlyAlert}>
          <InfoAlert message={lang.form_builder.mobileOnlyAlert} />
        </Box>
      )}
    </>
  );
});

FileSetting.displayName = 'FileSetting';
export { FileSetting };
